import type { Dispatch, SetStateAction } from "react";
import { useCallback, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import type { GridPaginationModel } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import type { UseContasReceberResult } from "../hooks/useContasReceber";
import type { ContaReceberView } from "../types/contas-receber.types";
import type { ContasReceberFiltros as CrFiltrosState } from "../types/contas-receber.types";
import FinanceiroErrorState from "./FinanceiroErrorState";
import FinanceiroSection from "./FinanceiroSection";
import ContasReceberFiltros from "./ContasReceberFiltros";
import ContasReceberResumoCards from "./ContasReceberResumoCards";
import ContasReceberTabela from "./ContasReceberTabela";

function CardsSkeleton() {
  return (
    <Grid container spacing={2}>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Grid item xs={12} sm={6} md={4} key={i}>
          <Skeleton variant="rounded" height={112} sx={{ borderRadius: 2 }} />
        </Grid>
      ))}
    </Grid>
  );
}

export type ContasReceberSectionProps = {
  contasReceber: UseContasReceberResult;
  onBaixarTitulo?: (titulo: ContaReceberView) => void;
  onCancelarTitulo?: (titulo: ContaReceberView) => void;
  /** Sem `FinanceiroSection` — página dedicada com `LojaPageContainer`. */
  embedded?: boolean;
};

export default function ContasReceberSection({
  contasReceber,
  onBaixarTitulo,
  onCancelarTitulo,
  embedded,
}: ContasReceberSectionProps) {
  const [periodoResetTick, setPeriodoResetTick] = useState(0);

  const handleLimpar = useCallback(() => {
    contasReceber.limparFiltros();
    setPeriodoResetTick((n) => n + 1);
  }, [contasReceber]);

  const handlePaginationModelChange = useCallback(
    (m: GridPaginationModel) => {
      contasReceber.setPagination(m.page + 1, m.pageSize);
    },
    [contasReceber],
  );

  const setFiltrosWrapper: Dispatch<SetStateAction<CrFiltrosState>> = useCallback(
    (action) => {
      contasReceber.setFiltros(action);
    },
    [contasReceber],
  );

  const { loading, error, isEmpty, isFiltered, data } = contasReceber;

  const corpo = (
      <Stack spacing={2}>
        <ContasReceberFiltros
          filtros={contasReceber.filtros}
          setFiltros={setFiltrosWrapper}
          onAplicar={contasReceber.aplicarFiltros}
          onLimpar={handleLimpar}
          loading={loading}
          resetPeriodoPresetTick={periodoResetTick}
        />

        {error ? (
          <FinanceiroErrorState message={error} onRetry={contasReceber.refetch} />
        ) : null}

        {!error && loading && !data ? <CardsSkeleton /> : null}

        {!error && data ? (
          <>
            <ContasReceberResumoCards resumo={data.resumo} loading={loading} />
            {!loading && isEmpty ? (
              <MDBox
                py={6}
                textAlign="center"
                sx={{
                  border: "1px dashed",
                  borderColor: "grey.300",
                  borderRadius: 2,
                  bgcolor: "grey.50",
                }}
              >
                <AccountBalanceWalletOutlinedIcon
                  sx={{ fontSize: 48, color: "grey.400", mb: 1 }}
                />
                <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                  Nenhum título a receber encontrado
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  {isFiltered
                    ? "Ajuste o período ou limpe os filtros para ver outros títulos."
                    : "Não há títulos a receber registrados no período atual."}
                </MDTypography>
              </MDBox>
            ) : null}
            {!isEmpty ? (
              <Box>
                <ContasReceberTabela
                  linhas={data.itens}
                  total={data.total}
                  page={contasReceber.page}
                  limit={contasReceber.limit}
                  loading={loading}
                  onPaginationModelChange={handlePaginationModelChange}
                  onBaixar={onBaixarTitulo}
                  onCancelar={onCancelarTitulo}
                />
              </Box>
            ) : null}
          </>
        ) : null}
      </Stack>
  );

  if (embedded) return corpo;

  return (
    <FinanceiroSection
      titulo="Contas a receber"
      subtitulo="Acompanhe recebíveis pendentes, vencidos e pagos da loja e eventos."
    >
      {corpo}
    </FinanceiroSection>
  );
}
